import {
  TextBoxSelectionData,
  TextBoxSelectionType,
} from "components/basic/textBoxSelection";
import { useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useRef, useEffect } from "react";

export function createScopedSetter<T, K extends keyof T>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  key: K,
): React.Dispatch<React.SetStateAction<T[K]>> {
  return (update) => {
    setState((prev) => ({
      ...prev,
      [key]:
        typeof update === "function"
          ? (update as (prevValue: T[K]) => T[K])(prev[key])
          : update,
    }));
  };
}

export const getSelectedOptions = (
  element: TextBoxSelectionData[] | string,
) => {
  if (typeof element === "string") {
    return <div>{element}</div>;
  }

  return element.map((element, index) => {
    if (!element.isChecked) return null;

    const parts = [element.optionName];

    if (
      element.type === TextBoxSelectionType.nameAndPrice ||
      element.type === TextBoxSelectionType.name
    ) {
      if (element.enteredName) parts.push(element.enteredName);
    }

    if (
      element.type === TextBoxSelectionType.nameAndPrice ||
      element.type === TextBoxSelectionType.price
    ) {
      parts.push(`$${element.price}`);
    }

    return <div key={`${element.optionName}-${index}`}>{parts.join(", ")}</div>;
  });
};

// Hook for managing edit mode state and data reversion
export function useEditMode<T>(
  data: T,
  setData: React.Dispatch<React.SetStateAction<T>>,
) {
  const [searchParams] = useSearchParams();
  const originalDataRef = useRef<T | null>(null);

  const isEditMode = useMemo(
    () =>
      searchParams.has("editMode") && searchParams.get("editMode") === "true",
    [searchParams],
  );

  // Store original data when entering edit mode, clear when exiting
  useEffect(() => {
    if (isEditMode) {
      if (!originalDataRef.current) {
        originalDataRef.current = JSON.parse(JSON.stringify(data));
      }
    } else {
      originalDataRef.current = null;
    }
  }, [data, isEditMode]);

  const handleSave = useCallback(() => {
    originalDataRef.current = null;
    // Check if serviceId exists to determine which review page to return to
    const serviceId = searchParams.get("serviceId");
    return serviceId
      ? `reviewAndUpdate&serviceId=${serviceId}`
      : "reviewAndLaunch";
  }, [searchParams]);

  const handleCancel = useCallback(() => {
    if (originalDataRef.current) {
      setData(originalDataRef.current);
      originalDataRef.current = null;
    }
    // Check if serviceId exists to determine which review page to return to
    const serviceId = searchParams.get("serviceId");
    return serviceId
      ? `reviewAndUpdate&serviceId=${serviceId}`
      : "reviewAndLaunch";
  }, [setData, searchParams]);

  return {
    isEditMode,
    handleSave,
    handleCancel,
  };
}
